import moment from 'moment';
import { useMemo, useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  fetchSendMetricsRequest,
  getFlexMetricsDateRange,
  getWorkflow,
} from 'modules/workflows/ducks';
import { BUTTON_SIZES, BUTTON_VARIANTS } from 'ui/button';
import { DateRangePicker } from 'ui/datePicker';
import { Calendar24, ChevronDown } from 'ui/icons';
import { ALIGNMENT, RAW_SELECT_DATA_PORTAL_ID } from 'ui/selects';
import { guessCurrentTimeZone } from 'utils/datetime';
import { useClickOutside } from 'utils/hooks/useClickOutside';
import styled from 'styled-components';
import {
  DatePickerContainer,
  DatePickerWrapper,
  FlexMetricsButton,
  InnerButtonContainer,
} from './FlexMetricsRangeSelect.styled';
import { Preset } from './FlexMetricsRangeSelect.types';
import { getPresets, getTriggerText, getPresetFromDateRange } from './FlexMetricsRangeSelect.utils';

export const FLEX_METRICS_SELECT_TEST_ID = 'flex-metrics-select';
export const FLEX_METRICS_TRIGGER_TEST_ID = 'flex-metrics-select-trigger';
const DEFAULT_PRESET = Preset.JourneyStart;
const defaultTimeZone = guessCurrentTimeZone();

const StyledDateRangePicker = styled(DateRangePicker)`
  background-color: ${({ theme }) => theme.colors.white};
`;

export const FlexMetricsRangeSelect = () => {
  const dispatch = useDispatch();
  const wrapperRef = useRef(null);

  const workflow = useSelector(getWorkflow);
  const { createdAt, updatedAt } = workflow;
  const dateRange = useSelector(getFlexMetricsDateRange);
  const { startDate = moment(), endDate = moment(), startDateString, endDateString } = dateRange;

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [timePreset, setTimePreset] = useState(DEFAULT_PRESET);

  const datepickerPresets = useMemo(
    () => getPresets(createdAt as string, updatedAt as string),
    [createdAt, updatedAt],
  );

  useEffect(() => {
    // If the date range changes externally, update the preset so that it
    // matches the date range selected.
    const updatedPreset = getPresetFromDateRange({
      dateRange,
      workflow,
      timeZone: defaultTimeZone,
    });
    setTimePreset(updatedPreset);
  }, [startDateString, endDateString]);

  useClickOutside(wrapperRef, () => {
    const timeZonePickerDropdownPortal = document.querySelector(
      `[data-portal-id="${RAW_SELECT_DATA_PORTAL_ID}"]`,
    ); // NOTE: only one of these should exist at a time

    /**
     * If the click originated from the time zone dropdown(i.e. the dropdown is open),
     * don't close the date picker. The dropdown portal must be close before we start acknowledging
     * clicks outside of the date picker.
     */
    if (timeZonePickerDropdownPortal) {
      return;
    }
    setShowDatePicker(false);
  });

  return (
    <DatePickerContainer data-test={FLEX_METRICS_SELECT_TEST_ID} ref={wrapperRef}>
      <FlexMetricsButton
        data-test={FLEX_METRICS_TRIGGER_TEST_ID}
        onClick={() => setShowDatePicker(!showDatePicker)}
        variant={BUTTON_VARIANTS.SECONDARY}
        size={BUTTON_SIZES.LARGE}
      >
        <InnerButtonContainer>
          <Calendar24 />
          {datepickerPresets[timePreset]?.filterLabel || getTriggerText(startDate, endDate)}
        </InnerButtonContainer>
        <ChevronDown height={16} />
      </FlexMetricsButton>

      {showDatePicker && (
        <DatePickerWrapper>
          <StyledDateRangePicker
            blockFutureDates
            dateRangePreset={timePreset}
            defaultTimeZone={defaultTimeZone}
            endDate={endDate}
            onDateRangeChange={(dateParams) =>
              dispatch(fetchSendMetricsRequest(workflow, dateParams))
            }
            onPresetChange={(val) => setTimePreset(val as Preset)}
            presets={datepickerPresets}
            promoteCustomInput
            showRelativeDatePresets
            startDate={startDate}
            timezoneHorizontalAlignment={ALIGNMENT.RIGHT}
            hideTimeInputs
            isTimeZonePickerDisabled
          />
        </DatePickerWrapper>
      )}
    </DatePickerContainer>
  );
};
